import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { getTierInfo, getBankTier } from '../../data/banks';
import { getTranslatedCreditBureauReporting, getBureauAbbreviations } from '../../data/creditBureauReporting';
import KebabMenu from './KebabMenu';
import CreditIncreaseStatusBar from './CreditIncreaseStatusBar';
import './CardList.css';

const CardItem = ({ card, onEdit, onDelete, formatCurrency, formatDate, formatPercentage }) => {
  const t = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  // Derive bankTier from bankName if bankTier is not set
  const bankTier = card.bankTier || (card.bankName ? getBankTier(card.bankName) : null);
  const tierInfo = bankTier ? getTierInfo(bankTier) : null;
  const bureauReporting = getTranslatedCreditBureauReporting(card.bankName, t);
  const bureauAbbreviations = getBureauAbbreviations(card.bankName);

  return (
    <div 
      className="card-item"
    >
      <div className="card-item-header">
        <div className="card-item-title">
          <h4>{card.bankName}</h4>
          {tierInfo && (
            <span
              className="card-tier-badge"
              style={{
                backgroundColor: tierInfo.bgColor,
                color: tierInfo.color,
              }}
              title={tierInfo.description}
            >
              {tierInfo.label}
            </span>
          )}
        </div>
        <div className={`card-item-actions visible`}>
          <KebabMenu
            onEdit={() => onEdit(card)}
            onDelete={() => onDelete(card.id)}
          />
        </div>
      </div>

      <div className="card-item-body">
        <div className="card-type-row">
          <span className="card-type-label">{card.cardSubType || 'N/A'}</span>
        </div>

        <div className="card-credit-line">
          <span className="card-credit-label">{t('creditCardsInventory.creditLimit')}</span>
          <span className="card-credit-value">
            {formatCurrency(card.creditLine)}
          </span>
        </div>
        
        <CreditIncreaseStatusBar card={card} />
        
        {bureauReporting && (
          <div className="card-bureau-reporting">
            <span className="card-bureau-label">{t('creditCardsInventory.pullsFrom')}</span>
            <div className="card-bureau-badges">
              {bureauAbbreviations.length > 0 ? (
                bureauAbbreviations.map((abbr, index) => (
                  <span key={index} className="bureau-badge" title={bureauReporting.bureaus[index]}>
                    {abbr}
                  </span>
                ))
              ) : (
                <span className="bureau-badge no-pull" title={t('creditCardsInventory.noCreditCheckTooltip')}>
                  {t('creditCardsInventory.noCreditCheck')}
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className="card-item-actions visible" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
          <span className="card-detail-label">{isExpanded ? t('creditCardsInventory.hideDetails') : t('creditCardsInventory.showDetails')}</span>
        </div>

        {isExpanded && (
          <div className="card-item-details">
            {card.apr && (
              <div className="card-detail-item">
                <span className="card-detail-label">{t('creditCardsInventory.apr')}</span>
                <span className="card-detail-value">{formatPercentage(card.apr)}</span>
              </div>
            )}

            {card.annualFee !== null && card.annualFee !== undefined && (
              <div className="card-detail-item">
                <span className="card-detail-label">{t('creditCardsInventory.annualFee')}</span>
                <span className="card-detail-value">{formatCurrency(card.annualFee)}</span>
              </div>
            )}

            {card.rewardsProgram && (
              <div className="card-detail-item">
                <span className="card-detail-label">{t('creditCardsInventory.rewards')}</span>
                <span className="card-detail-value">{card.rewardsProgram}</span>
              </div>
            )}

            <div className="card-detail-item">
              <span className="card-detail-label">{t('creditCardsInventory.created')}</span>
              <span className="card-detail-value">{formatDate(card.dateCreated)}</span>
            </div>

            {card.last4Digits && (
              <div className="card-detail-item">
                <span className="card-detail-label">{t('creditCardsInventory.last4')}</span>
                <span className="card-detail-value">****{card.last4Digits}</span>
              </div>
            )}

            {card.expirationDate && (
              <div className="card-detail-item">
                <span className="card-detail-label">{t('creditCardsInventory.expires')}</span>
                <span className="card-detail-value">{formatDate(card.expirationDate)}</span>
              </div>
            )}

            {bureauReporting && (
              <div className="card-detail-section">
                <div className="card-detail-item">
                  <span className="card-detail-label">{t('creditCardsInventory.creditBureauPull')}</span>
                  <span className="card-detail-value">{bureauReporting.reportingType}</span>
                </div>
                {bureauReporting.frequency && (
                  <div className="card-detail-item">
                    <span className="card-detail-label">{t('creditCardsInventory.whenTheyPull')}</span>
                    <span className="card-detail-value">{bureauReporting.frequency}</span>
                  </div>
                )}
                {bureauReporting.notes && (
                  <div className="card-detail-item card-bureau-notes">
                    <span className="card-detail-label">{t('creditCardsInventory.importantNotes')}</span>
                    <span className="card-detail-value">{bureauReporting.notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CardList = ({ cards, onEdit, onDelete, isLoading = false }) => {
  const t = useTranslation();
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (date.toDate) {
        return date.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
      return 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <div className="card-list-loading">
        <div className="spinner"></div>
        <p>{t('creditCardsInventory.loadingCards')}</p>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="card-list-empty">
        <div className="empty-icon">💳</div>
        <h3>{t('creditCardsInventory.noCardsYet')}</h3>
        <p>{t('creditCardsInventory.noCardsMessage')}</p>
      </div>
    );
  }

  return (
    <div className="card-list">
      {cards.map((card) => (
        <CardItem
          key={card.id}
          card={card}
          onEdit={onEdit}
          onDelete={onDelete}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatPercentage={formatPercentage}
        />
      ))}
    </div>
  );
};

export default CardList;

